import { useQueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';
import { Navigate, Outlet, Route, Routes, useLocation, useNavigate } from 'react-router';
import { EVENTO_SESSAO_EXPIRADA } from './api/cliente';
import { Aviso } from './components/Aviso';
import { Layout } from './components/Layout';
import { Carregando } from './components/Pagina';
import { CHAVE_SESSAO, useSessao } from './hooks/useSessao';
import { Custos } from './pages/Custos';
import { Login } from './pages/Login';
import { NovaRodada } from './pages/NovaRodada';
import { PdfTabela } from './pages/PdfTabela';
import { Previa } from './pages/Previa';
import { Rodadas } from './pages/Rodadas';
import { TrocarSenha } from './pages/TrocarSenha';
import { Conexoes } from './pages/admin/Conexoes';
import { Mensagens } from './pages/admin/Mensagens';
import { Usuarios } from './pages/admin/Usuarios';

/** Área logada: exige sessão e, com senha temporária, só libera a troca de senha. */
function AreaLogada() {
  const { data: sessao, isPending, error } = useSessao();
  const local = useLocation();

  if (isPending) return <Carregando texto="Abrindo…" />;
  if (error) {
    return (
      <div className="mx-auto mt-16 max-w-md px-4">
        <Aviso tipo="erro" titulo="Não foi possível abrir o sistema">
          {error.message}
        </Aviso>
      </div>
    );
  }
  if (!sessao) return <Navigate to="/login" replace state={{ de: local.pathname }} />;
  if (sessao.usuario.deveTrocarSenha && local.pathname !== '/trocar-senha') return <Navigate to="/trocar-senha" replace />;
  return <Layout sessao={sessao} />;
}

function SomenteAdmin() {
  const { data: sessao } = useSessao();
  return sessao?.usuario.perfil === 'ADMIN' ? <Outlet /> : <Navigate to="/" replace />;
}

/** Quando qualquer chamada recebe 401, limpa a sessão e volta para o login. */
function VigiaSessao() {
  const cliente = useQueryClient();
  const navegar = useNavigate();
  useEffect(() => {
    const aoExpirar = () => {
      cliente.setQueryData(CHAVE_SESSAO, null);
      navegar('/login', { replace: true });
    };
    window.addEventListener(EVENTO_SESSAO_EXPIRADA, aoExpirar);
    return () => window.removeEventListener(EVENTO_SESSAO_EXPIRADA, aoExpirar);
  }, [cliente, navegar]);
  return null;
}

export function App() {
  return (
    <>
      <VigiaSessao />
      <Routes>
        <Route path="/login" element={<Login />} />
        <Route element={<AreaLogada />}>
          <Route index element={<Navigate to="/rodadas" replace />} />
          <Route path="/trocar-senha" element={<TrocarSenha />} />
          <Route path="/rodadas" element={<Rodadas />} />
          <Route path="/rodadas/nova" element={<NovaRodada />} />
          <Route path="/rodadas/:id" element={<Previa />} />
          <Route path="/custos" element={<Custos />} />
          <Route path="/pdf" element={<PdfTabela />} />
          <Route path="/admin" element={<SomenteAdmin />}>
            <Route index element={<Navigate to="/admin/usuarios" replace />} />
            <Route path="usuarios" element={<Usuarios />} />
            <Route path="conexoes" element={<Conexoes />} />
            <Route path="mensagens" element={<Mensagens />} />
          </Route>
          <Route path="*" element={<Navigate to="/" replace />} />
        </Route>
      </Routes>
    </>
  );
}
